import React from "react";
import { Handshake, MessageCircle, Package, Search, User } from "lucide-react";
import { ChatContext, Notification, ProposalContext, TradeContext, } from "modules/chat/model/chat";
import { cn } from "utils/utils";

type AnyContext = ChatContext | TradeContext | ProposalContext | null | undefined;

// Type guards for contexts
export function isChatContext(
  context: AnyContext
): context is ChatContext {
  return context != null && typeof context === "object" && "chatId" in context;
}

export function isTradeContext(
  context: AnyContext
): context is TradeContext {
  return (
    context != null &&
    typeof context === "object" &&
    "type" in context &&
    ("tradeItemId" in context || "contentHash" in context)
  );
}

export function isProposalContext(
  context: AnyContext
): context is ProposalContext {
  return context != null && typeof context === "object" && "chatId" in context;
}

// Type guards for notifications
export function isChatNotification(
  notification: Notification
): notification is Notification & { context: ChatContext } {
  return (
    notification.domain === "chat" &&
    notification.context != null &&
    isChatContext(notification.context)
  );
}

export function isTradeNotification(
  notification: Notification
): notification is Notification & { context: TradeContext } {
  return (
    notification.domain === "trade" &&
    notification.context != null &&
    isTradeContext(notification.context)
  );
}

export function isProposalNotification(
  notification: Notification
): notification is Notification & { context: ProposalContext } {
  return (
    notification.domain === "proposal" &&
    notification.context != null &&
    isProposalContext(notification.context)
  );
}

// Context extractors with type safety
export function getChatContext(notification: Notification): ChatContext | null {
  if (isChatNotification(notification)) {
    return notification.context;
  }
  return null;
}

export function getTradeContext(
  notification: Notification
): TradeContext | null {
  if (isTradeNotification(notification)) {
    return notification.context;
  }
  return null;
}

export function getProposalContext(
  notification: Notification
): ProposalContext | null {
  if (isProposalNotification(notification)) {
    return notification.context;
  }
  return null;
}

// Shared formatting functions
export function formatNotificationMessage(
  notification: Notification,
  fallbackMessage?: string
): string {
  if (isProposalNotification(notification)) {
    return notification.message || "Trade from a partner";
  } else if (isChatNotification(notification)) {
    return notification.message || "Sent you a message";
  } else if (isTradeNotification(notification)) {
    const tradeContext = getTradeContext(notification);
    if (tradeContext) {
      const { cardName, store, ownerName, type, quantity, matched_cards } =
        tradeContext;
      const quantityText = quantity && quantity > 1 ? `${quantity}x ` : "";

      if (type === "trade-item-available") {
        return cardName && store?.name && ownerName
          ? `${ownerName} has ${quantityText}${cardName} available at ${store.name}`
          : fallbackMessage || notification.message;
      } else if (type === "trade-item-wanted") {
        return cardName && store?.name && ownerName
          ? `${ownerName} wants ${quantityText}${cardName} at ${store.name}`
          : fallbackMessage || notification.message;
      } else if (type === "trade-item-wanted-bulk") {
        const cardCount = matched_cards?.length || quantity;
        return store?.name && ownerName
          ? `${ownerName} wants ${cardCount} cards from your collection at ${store.name}`
          : fallbackMessage || notification.message;
      } else if (type === "trade-item-available-bulk") {
        const cardCount = matched_cards?.length || quantity;
        return store?.name && ownerName
          ? `${ownerName} has ${cardCount} cards you want available at ${store.name}`
          : fallbackMessage || notification.message;
      }
    }
  }
  return fallbackMessage || notification.message;
}

// City-scoped trade matches carry no store (see the API's
// trade-notification-service: store is nil for nearby-store city matches).
export function isCityTradeNotification(notification: Notification): boolean {
  return isTradeNotification(notification) && !getTradeContext(notification)?.store;
}

export function getNotificationRoute(
  notification: Notification,
  viewerCityMatchedRoute?: string | null
): string {
  if (isProposalNotification(notification)) {
    const proposalContext = getProposalContext(notification);
    return proposalContext?.chatId
      ? `/x/inbox/${proposalContext.chatId}`
      : "/x/inbox";
  } else if (isChatNotification(notification)) {
    const chatContext = getChatContext(notification);
    return chatContext?.chatId ? `/x/inbox/${chatContext.chatId}` : "/x/inbox";
  } else if (isTradeNotification(notification)) {
    // City-wide match: send the viewer to their city's matched page when the
    // city is unambiguous. The pairwise fallback includes both store and city
    // scopes for this specific counterparty.
    if (isCityTradeNotification(notification) && viewerCityMatchedRoute) {
      return viewerCityMatchedRoute;
    }
    const senderUsername = notification.sender?.username?.trim();
    if (
      senderUsername &&
      !senderUsername.includes("[") &&
      !senderUsername.includes("]")
    ) {
      return `/users/${encodeURIComponent(senderUsername)}/matched`;
    }
    const tradeContext = getTradeContext(notification);
    return tradeContext?.store?.slug
      ? `/stores/${tradeContext.store.slug}`
      : "/x/notifications";
  }
  return "/x/notifications";
}

export type NotificationIconSet = "notification" | "toast";

export function getNotificationIconProps(
  notification: Notification,
  iconSet: NotificationIconSet = "notification",
  size?: number | string
) {
  const defaultSize = size || 20;

  if (isProposalNotification(notification)) {
    return {
      icon: "Handshake",
      className: iconSet === "toast" ? "text-base-100" : "text-accent",
      size: defaultSize,
    };
  } else if (isChatNotification(notification)) {
    return {
      icon: "MessageCircle",
      className: "text-primary",
      size: defaultSize,
    };
  } else if (isTradeNotification(notification)) {
    const tradeContext = getTradeContext(notification);
    if (
      tradeContext?.type === "trade-item-available" ||
      tradeContext?.type === "trade-item-available-bulk"
    ) {
      return {
        icon: "Package",
        className: iconSet === "toast" ? "text-base-100" : "text-warning",
        size: defaultSize,
      };
    } else {
      return {
        icon: "Search",
        className: iconSet === "toast" ? "text-base-100" : "text-secondary",
        size: defaultSize,
      };
    }
  }
  return {
    icon: "User",
    className: "text-base-content",
    size: defaultSize,
  };
}

export type NotificationTitleFormat = "simple" | "detailed";

export function getNotificationTitle(
  notification: Notification,
  format: NotificationTitleFormat = "simple"
): string {
  if (isProposalNotification(notification)) {
    return format === "detailed"
      ? `Trade from ${notification.sender.username}`
      : "Trade";
  } else if (isChatNotification(notification)) {
    return `Message from ${notification.sender.username}`;
  } else if (isTradeNotification(notification)) {
    const tradeContext = getTradeContext(notification);
    if (!tradeContext) {
      return format === "detailed"
        ? `Trade Notification from ${notification.sender.username}`
        : "Trade notification";
    }

    if (format === "detailed") {
      switch (tradeContext.type) {
        case "trade-item-available":
          return `Card Available - ${tradeContext.cardName || "Trading Card"}`;
        case "trade-item-wanted":
          return `Card Wanted - ${tradeContext.cardName || "Trading Card"}`;
        case "trade-item-wanted-bulk": {
          const wantedCount =
            tradeContext.matched_cards?.length || tradeContext.quantity;
          return `Multiple Cards Wanted - ${wantedCount} cards`;
        }
        case "trade-item-available-bulk": {
          const wantAvailableCount =
            tradeContext.matched_cards?.length || tradeContext.quantity;
          return `Multiple Cards Available - ${wantAvailableCount} cards`;
        }
        default:
          return `Trade Notification from ${notification.sender.username}`;
      }
    } else {
      switch (tradeContext.type) {
        case "trade-item-available":
          return "Card available";
        case "trade-item-wanted":
          return "Card wanted";
        case "trade-item-wanted-bulk":
          return "Multiple cards wanted";
        case "trade-item-available-bulk":
          return "Multiple cards available";
        default:
          return "Trade notification";
      }
    }
  }
  return format === "detailed"
    ? `Notification from ${notification.sender.username}`
    : "Notification";
}

export function getNotificationColors(notification: Notification): string {
  if (isProposalNotification(notification)) {
    return "text-accent";
  } else if (isChatNotification(notification)) {
    return "text-primary";
  } else if (isTradeNotification(notification)) {
    const tradeContext = getTradeContext(notification);
    if (
      tradeContext?.type === "trade-item-available" ||
      tradeContext?.type === "trade-item-available-bulk"
    ) {
      return "text-warning";
    } else {
      return "text-secondary";
    }
  }
  return "text-base-content";
}

// Unified icon mappings used across all components
const ICON_MAP = {
  Handshake,
  MessageCircle,
  Package,
  Search,
  User,
};

export function getNotificationIcon(
  notification: Notification,
  iconSet: NotificationIconSet = "toast",
  size?: number | string,
  className?: string
): React.ReactElement {
  const iconProps = getNotificationIconProps(notification, iconSet, size);

  const IconComponent = ICON_MAP[iconProps.icon as keyof typeof ICON_MAP];

  // Fallback to User icon if component not found
  const Component = IconComponent || User;

  const iconSize = size || iconProps.size;
  const iconClassName = className
    ? cn(iconProps.className, className)
    : cn(iconProps.className);

  return React.createElement(Component, {
    size: iconSize,
    className: iconClassName,
  });
}
